import Header from "../../../shared/app-shell/Header";
import Sidebar from "../../../shared/app-shell/Sidebar";
import { themeAtom } from "../../../shared/atoms";
import { log, logE, logV } from "../../../shared/services/debug-logger";
import FuelCurveCalculator from "../components/FuelCurveCalculator";
import PerformanceIndicatorCalculator from "../components/PerformanceIndicatorCalculator";
import StandaloneDraftCalculator from "../components/StandaloneDraftCalculator";
import { CalculatorsRequest } from "../models/calculators.models";
import { calculateCalculators, getCalculatorsErrorMessage } from "../services/calculators.service";
import { calculatorsStateAtom, calculatorsUseMockDataAtom } from "../state/calculators.atoms";
import { clearPerformanceInputs } from "../utils/calculators.utils";
import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";
import { twMerge } from "tailwind-merge";

// Coordinates calculation state while child components own each visible Power BI calculator section.
const CalculatorsPage = () => {
  const theme = useAtomValue(themeAtom);
  const useMockData = useAtomValue(calculatorsUseMockDataAtom);
  const [calculatorsState, setCalculatorsState] = useAtom(calculatorsStateAtom);
  logV("CalculatorsPage - BUILD", { calculating: calculatorsState.isCalculating, error: !!calculatorsState.error });

  const updateRequest = (request: CalculatorsRequest) => {
    setCalculatorsState((current) => ({ ...current, request, response: null, error: null }));
  };

  // Recomputes after slicer-style edits and ignores stale replies when inputs change quickly.
  useEffect(() => {
    let isCurrentRequest = true;
    const request = calculatorsState.request;
    const calculationTimer = window.setTimeout(async () => {
      setCalculatorsState((current) => ({ ...current, isCalculating: true, error: null }));

      try {
        const response = await calculateCalculators(request, useMockData);

        if (isCurrentRequest) {
          logV("STATE - calculateCalculators - apply", { warnings: response.warnings.length });
          setCalculatorsState((current) => ({ ...current, response, isCalculating: false }));
        }
      } catch (error) {
        if (isCurrentRequest) {
          logE(new Error("CalculatorsPage - calculateCalculators - failed"));
          setCalculatorsState((current) => ({
            ...current,
            response: null,
            isCalculating: false,
            error: getCalculatorsErrorMessage(error),
          }));
        }
      }
    }, 250);

    return () => {
      isCurrentRequest = false;
      window.clearTimeout(calculationTimer);
    };
  }, [calculatorsState.request, setCalculatorsState, useMockData]);

  const resetPerformanceInputs = () => {
    log("CalculatorsPage - resetPerformanceInputs");
    setCalculatorsState((current) => ({
      ...current,
      request: clearPerformanceInputs(current.request),
      response: current.response ? { ...current.response, performance: [] } : null,
      error: null,
    }));
  };

  return (
    <div className="flex h-screen">
      <Sidebar />
      <div className={twMerge("flex min-w-0 flex-1 flex-col overflow-hidden", theme.home.tailwind.pageBackground)}>
        <Header />

        <main className="flex-1 overflow-y-auto px-4 pb-8 pt-3 sm:px-6">
          <div className="mx-auto max-w-[1500px] overflow-hidden rounded-lg border border-gray-300 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-3 dark:border-gray-700">
              <div>
                <h1 className="text-xl font-semibold text-gray-950 dark:text-white">Calculators</h1>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {useMockData ? "Modeled local mock formulas" : "Live backend calculations"}
                </p>
              </div>
              <span data-test="calculators-calculation-status" className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {calculatorsState.isCalculating ? "Updating..." : "Ready"}
              </span>
            </div>

            {calculatorsState.error && (
              <div data-test="calculators-error" role="alert" className="border-b border-red-200 bg-red-50 px-5 py-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
                {calculatorsState.error}
              </div>
            )}

            {calculatorsState.response?.warnings?.length ? (
              <div data-test="calculators-warnings" className="border-b border-amber-200 bg-amber-50 px-5 py-2 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                {calculatorsState.response.warnings.join(" ")}
              </div>
            ) : null}

            <div className="grid grid-cols-1 lg:grid-cols-2">
              <StandaloneDraftCalculator
                draft={calculatorsState.request.draft}
                normalizedStw={calculatorsState.response?.normalizedStw}
                onChange={(draft) => updateRequest({ ...calculatorsState.request, draft })}
              />
              <FuelCurveCalculator
                fuelCurve={calculatorsState.request.fuelCurve}
                ladenRows={calculatorsState.response?.ladenCurve ?? []}
                ballastRows={calculatorsState.response?.ballastCurve ?? []}
                onChange={(fuelCurve) => updateRequest({ ...calculatorsState.request, fuelCurve })}
              />
            </div>

            <PerformanceIndicatorCalculator
              rows={calculatorsState.request.performance.rows}
              results={calculatorsState.response?.performance ?? []}
              onChange={(rows) => updateRequest({ ...calculatorsState.request, performance: { rows } })}
              onReset={resetPerformanceInputs}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default CalculatorsPage;
